// ============================================================================
// Noyau — DÉVELOPPEMENTS LIMITÉS en 0 (refonte, lot 6.63, moteur v58).
// ============================================================================
// Un développement limité est une suite de coefficients EXACTS (rationnels) et un reste :
//
//   1 + x + x² ÷ 2 + o(x²)   →   { 0 : 1, 1 : 1, 2 : 1/2 }, reste o(x²)
//
// Les coefficients connus sont ceux des degrés ≤ e, le reste est o(xᵉ) ; e = +∞ : écriture
// exacte (un polynôme). Les degrés négatifs sont permis (1 ÷ sin x = 1 ÷ x + x ÷ 6 + o(x)).
//
// Dans une écriture, « o(xᵏ) » est lu comme le symbole « oₖ » (o₀ = o(1)) : un reste, sans
// coefficient connu. Les opérations suivent les règles du cours :
//
//   somme : le reste est le plus gros des deux ;
//   produit : (A + o(xᵃ))(B + o(xᵇ)) = AB + o(x^min(a + val B, b + val A)) ;
//   quotient : 1 ÷ (c xᵛ(1 + w)) = x⁻ᵛ ÷ c × (1 − w + w² − …), le reste perd 2v ;
//   composée : f(u) avec u → 0 (eˣ, sin, cos, tan, arctan), ln(1 + w), (1 + w)^a.
//
// Une écriture exacte (eˣ, sin x…) se développe jusqu'à un ordre N donné. Un coefficient
// irrationnel (e^(1 + x), ln 2…) n'est pas calculé : null.
// ============================================================================

import * as Q from './exact';
import type { Rat } from './exact';
import type { Term } from './term';

export interface Ser {
  /** coefficients non nuls connus : degré → coefficient (tous ≤ e) */
  co: Map<number, Rat>;
  /** reste o(xᵉ) ; +∞ : écriture exacte */
  e: number;
}

/* ------------------------------ les restes « oₖ » ------------------------------ */

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const SUP = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/** le symbole d'un reste : o(1) → « o₀ », o(x²) → « o₂ » */
export function oSym(k: number): string {
  return 'o' + String(k).replace(/\d/g, (d) => SUB[Number(d)]!);
}

/** l'ordre d'un symbole de reste (« o₂ » → 2), null sinon */
export function oOrder(name: string): number | null {
  const m = /^o([₀₁₂₃₄₅₆₇₈₉]+)$/.exec(name);
  if (!m) return null;
  return Number([...m[1]!].map((c) => SUB.indexOf(c)).join(''));
}

/** l'écriture d'un reste pour la variable x : o(1), o(x), o(x²) */
export function oText(k: number, x = 'x'): string {
  if (k === 0) return 'o(1)';
  if (k === 1) return `o(${x})`;
  return `o(${x}${String(k).replace(/\d/g, (d) => SUP[Number(d)]!)})`;
}

/** un bout « o(x²) », « o(1) », « o(x) » : son ordre, null sinon */
export function oAtomOrder(atom: string): number | null {
  const m = /^o\((1|[a-zA-Z](?:([⁰¹²³⁴⁵⁶⁷⁸⁹]+))?)\)$/.exec(atom);
  if (!m) return null;
  if (m[1] === '1') return 0;
  if (!m[2]) return 1;
  return Number([...m[2]].map((c) => SUP.indexOf(c)).join(''));
}

/** l'écriture contient-elle un reste « oₖ » ? */
export function hasO(t: Term): boolean {
  switch (t.k) {
    case 'sym':
      return oOrder(t.n) !== null;
    case 'num':
      return false;
    case 'add':
    case 'mul':
      return t.c.some(hasO);
    case 'fn':
      return hasO(t.a);
    default:
      return hasO(t.a) || hasO(t.b);
  }
}

/* ------------------------------ opérations ------------------------------ */

const R0 = Q.ZERO;
const R1 = Q.ONE;

export const serConst = (v: Rat): Ser => ({
  co: Q.isZero(v) ? new Map() : new Map([[0, v]]),
  e: Infinity,
});
const serX = (): Ser => ({ co: new Map([[1, R1]]), e: Infinity });
const serRem = (k: number): Ser => ({ co: new Map(), e: k });

export function coef(s: Ser, d: number): Rat {
  return s.co.get(d) ?? R0;
}

/** le plus petit degré dont le coefficient est connu et non nul */
export function serVal(s: Ser): number | null {
  let v: number | null = null;
  for (const [d, c] of s.co) if (!Q.isZero(c) && (v === null || d < v)) v = d;
  return v;
}

/** borne basse de l'ordre (un reste pur o(xᵉ) : e) */
function lowBound(s: Ser): number {
  const v = serVal(s);
  return v === null ? s.e : v;
}

export function trunc(s: Ser, e: number): Ser {
  const ne = Math.min(s.e, e);
  const co = new Map<number, Rat>();
  for (const [d, c] of s.co) if (d <= ne && !Q.isZero(c)) co.set(d, c);
  return { co, e: ne };
}

export function sAdd(a: Ser, b: Ser): Ser {
  const e = Math.min(a.e, b.e);
  const co = new Map<number, Rat>();
  for (const s of [a, b])
    for (const [d, c] of s.co) {
      if (d > e) continue;
      const v = Q.add(co.get(d) ?? R0, c);
      if (Q.isZero(v)) co.delete(d);
      else co.set(d, v);
    }
  return { co, e };
}

export function sScale(a: Ser, r: Rat): Ser {
  if (Q.isZero(r)) return { co: new Map(), e: Infinity };
  const co = new Map<number, Rat>();
  for (const [d, c] of a.co) co.set(d, Q.mul(c, r));
  return { co, e: a.e };
}

export const sNeg = (a: Ser): Ser => sScale(a, Q.rat(-1));

/** décaler les degrés : × xᵏ */
export function sShift(a: Ser, k: number): Ser {
  const co = new Map<number, Rat>();
  for (const [d, c] of a.co) co.set(d + k, c);
  return { co, e: a.e + k };
}

export function sMul(a: Ser, b: Ser): Ser {
  // un zéro exact
  if (!a.co.size && a.e === Infinity) return a;
  if (!b.co.size && b.e === Infinity) return b;
  const e = Math.min(a.e + lowBound(b), b.e + lowBound(a));
  const co = new Map<number, Rat>();
  for (const [da, ca] of a.co)
    for (const [db, cb] of b.co) {
      const d = da + db;
      if (d > e) continue;
      const v = Q.add(co.get(d) ?? R0, Q.mul(ca, cb));
      if (Q.isZero(v)) co.delete(d);
      else co.set(d, v);
    }
  return { co, e };
}

/** Σ a_k wᵏ (k = 0…), w de valuation ≥ 1, jusqu'au degré E (le reste suit) */
function sumPowers(w: Ser, a: (k: number) => Rat, E: number): Ser {
  let out: Ser = serConst(a(0));
  let term: Ser = serConst(R1);
  for (let k = 1; k <= Math.max(E, 0) + 2; k++) {
    term = trunc(sMul(term, w), E);
    // un coefficient nul : le terme (et son reste) disparaît
    const ak = a(k);
    if (!Q.isZero(ak)) out = sAdd(out, sScale(term, ak));
    if (serVal(term) === null && term.e >= E) break;
  }
  return trunc(out, E);
}

/** 1 ÷ b ; null si le premier terme de b n'est pas connu */
export function sInv(b: Ser, N: number): Ser | null {
  const v = serVal(b);
  if (v === null) return null;
  const c0 = b.co.get(v)!;
  const w: Ser = { co: new Map(), e: b.e - v };
  for (const [d, c] of b.co) if (d !== v) w.co.set(d - v, Q.div(c, c0)!);
  // un monôme exact : 1 ÷ (c xᵛ) est exact
  if (!w.co.size && w.e === Infinity) return { co: new Map([[-v, Q.div(R1, c0)!]]), e: Infinity };
  const E1 = Math.min(w.e, N + v);
  // 1 ÷ (1 + w) = Σ (−w)ᵏ
  const S = sumPowers(sNeg(w), () => R1, E1);
  return sShift(sScale(S, Q.div(R1, c0)!), -v);
}

export function sPowInt(a: Ser, n: number, N: number): Ser | null {
  if (!Number.isInteger(n) || Math.abs(n) > 24) return null;
  if (n < 0) {
    const p = sPowInt(a, -n, N);
    return p ? sInv(p, N) : null;
  }
  let out: Ser = serConst(R1);
  for (let i = 0; i < n; i++) out = sMul(out, a);
  return out;
}

/** le coefficient constant, s'il est connu (reste d'ordre ≥ 0) ; null si un degré < 0 */
function constOf(u: Ser): Rat | null {
  for (const [d, c] of u.co) if (d < 0 && !Q.isZero(c)) return null;
  if (u.e < 0) return null;
  return coef(u, 0);
}

/** f(u) = Σ a_k uᵏ pour u → 0 (pas de terme constant, pas de degré négatif) */
function compose0(u: Ser, a: (k: number) => Rat, N: number): Ser | null {
  const c = constOf(u);
  if (c === null || !Q.isZero(c)) return null;
  const vU = lowBound(u);
  if (vU < 1) {
    // u = o(1) : f(u) = a₀ + o(1) (a₁ ≠ 0) ou mieux
    if (u.e < 0) return null;
    return { co: Q.isZero(a(0)) ? new Map() : new Map([[0, a(0)]]), e: Math.min(u.e, N) };
  }
  const out = sumPowers(u, a, N);
  return trunc(out, N);
}

const FACT: bigint[] = (() => {
  const f = [1n];
  for (let i = 1; i <= 40; i++) f.push(f[i - 1]! * BigInt(i));
  return f;
})();
const invFact = (k: number): Rat => Q.rat(1n, FACT[k]!);

/** binom(r, k) pour r rationnel */
function binomRat(r: Rat, k: number): Rat {
  let out = R1;
  for (let i = 0; i < k; i++) out = Q.mul(out, Q.div(Q.sub(r, Q.rat(i)), Q.rat(i + 1))!);
  return out;
}

/** racine q-ième exacte d'un entier ≥ 0 (null sinon) */
function iroot(n: bigint, q: number): bigint | null {
  if (n < 0n) return null;
  if (n < 2n) return n;
  let lo = 0n;
  let hi = 1n;
  while (hi ** BigInt(q) <= n) hi *= 2n;
  while (lo < hi) {
    const m = (lo + hi + 1n) / 2n;
    if (m ** BigInt(q) <= n) lo = m;
    else hi = m - 1n;
  }
  return lo ** BigInt(q) === n ? lo : null;
}

/** c^r exact (c > 0 rationnel, r rationnel), null sinon */
function ratPow(c: Rat, r: Rat): Rat | null {
  if (Q.sign(c) <= 0) return null;
  const p = Number(r.n);
  const q = Number(r.d);
  if (Math.abs(p) > 24 || q > 12) return null;
  const n = iroot(c.n, q);
  const d = iroot(c.d, q);
  if (n === null || d === null) return null;
  return Q.pow(Q.rat(n, d), p);
}

/** u^r, r rationnel non entier : u = c xᵛ (1 + w) */
function sPowRat(u: Ser, r: Rat, N: number): Ser | null {
  const v = serVal(u);
  if (v === null) return null;
  const c0 = u.co.get(v)!;
  const shift = Q.mul(Q.rat(v), r);
  if (!Q.isInteger(shift)) return null;
  const cr = ratPow(c0, r);
  if (!cr) return null;
  const w: Ser = { co: new Map(), e: u.e - v };
  for (const [d, c] of u.co) if (d !== v) w.co.set(d - v, Q.div(c, c0)!);
  const s = Number(shift.n);
  const E1 = Math.min(w.e, N - s);
  const S = sumPowers(w, (k) => binomRat(r, k), E1);
  return sShift(sScale(S, cr), s);
}

function sExp(u: Ser, N: number): Ser | null {
  return compose0(u, invFact, N);
}
function sSin(u: Ser, N: number): Ser | null {
  return compose0(u, (k) => (k % 2 ? Q.mul(invFact(k), Q.rat(k % 4 === 1 ? 1 : -1)) : R0), N);
}
function sCos(u: Ser, N: number): Ser | null {
  return compose0(u, (k) => (k % 2 ? R0 : Q.mul(invFact(k), Q.rat(k % 4 === 0 ? 1 : -1))), N);
}
function sArctan(u: Ser, N: number): Ser | null {
  return compose0(u, (k) => (k % 2 ? Q.rat(k % 4 === 1 ? 1 : -1, k) : R0), N);
}
/** ln(u) avec u → 1 : ln(1 + w) = w − w² ÷ 2 + … */
function sLn(u: Ser, N: number): Ser | null {
  const c = constOf(u);
  if (c === null || !Q.eq(c, R1)) return null;
  const w = sAdd(u, serConst(Q.rat(-1)));
  return compose0(w, (k) => (k === 0 ? R0 : Q.rat(k % 2 ? 1 : -1, k)), N);
}

/* ------------------------------ une écriture ------------------------------ */

/** une fonction usuelle appliquée à un développement (null : pas en 0, ou irrationnel) */
export function serFn(f: string, u: Ser, N: number): Ser | null {
  switch (f) {
    case 'exp':
      return sExp(u, N);
    case 'sin':
      return sSin(u, N);
    case 'cos':
      return sCos(u, N);
    case 'tan': {
      const s = sSin(u, N + 2);
      const c = sCos(u, N + 2);
      if (!s || !c) return null;
      const ic = sInv(c, N + 2);
      return ic ? trunc(sMul(s, ic), N) : null;
    }
    case 'arctan':
      return sArctan(u, N);
    case 'ln':
      return sLn(u, N);
    case 'sqrt':
      return sPowRat(u, Q.rat(1, 2), N);
    default:
      return null;
  }
}

/** uʳ, r rationnel (entier ou non) */
export function serPow(u: Ser, r: Rat, N: number): Ser | null {
  if (Q.isInteger(r)) return sPowInt(u, Number(r.n), N);
  return sPowRat(u, r, N);
}

/** la valeur rationnelle d'une écriture sans variable (null sinon) */
export function termRat(t: Term): Rat | null {
  return numOf(t);
}

function numOf(t: Term): Rat | null {
  if (t.k === 'num') return t.v;
  if (t.k === 'mul') {
    let r = R1;
    for (const c of t.c) {
      const v = numOf(c);
      if (!v) return null;
      r = Q.mul(r, v);
    }
    return r;
  }
  if (t.k === 'div') {
    const a = numOf(t.a);
    const b = numOf(t.b);
    return a && b ? Q.div(a, b) : null;
  }
  if (t.k === 'add') {
    let r = R0;
    for (const c of t.c) {
      const v = numOf(c);
      if (!v) return null;
      r = Q.add(r, v);
    }
    return r;
  }
  return null;
}

/**
 * Le développement en 0 de l'écriture t (variable x), les parties exactes développées
 * jusqu'au degré N ; les restes « oₖ » sont des o(xᵏ). null : pas calculable (coefficient
 * irrationnel, forme non prévue).
 */
export function seriesOf(t: Term, x: string, N: number): Ser | null {
  try {
    return ser(t, x, N);
  } catch {
    return null;
  }
}

function ser(t: Term, x: string, N: number): Ser | null {
  switch (t.k) {
    case 'num':
      return serConst(t.v);
    case 'sym': {
      if (t.n === x) return serX();
      const k = oOrder(t.n);
      if (k !== null) return serRem(k);
      return null;
    }
    case 'add': {
      let out: Ser = serConst(R0);
      for (const c of t.c) {
        const s = ser(c, x, N);
        if (!s) return null;
        out = sAdd(out, s);
      }
      return out;
    }
    case 'mul': {
      let out: Ser = serConst(R1);
      for (const c of t.c) {
        const s = ser(c, x, N);
        if (!s) return null;
        out = sMul(out, s);
      }
      return out;
    }
    case 'div': {
      const a = ser(t.a, x, N);
      const b = ser(t.b, x, N);
      if (!a || !b) return null;
      const inv = sInv(b, N + Math.max(0, -lowBound(a)));
      return inv ? sMul(a, inv) : null;
    }
    case 'pow': {
      const r = numOf(t.b);
      if (r) {
        const a = ser(t.a, x, N);
        if (!a) return null;
        if (Q.isInteger(r)) return sPowInt(a, Number(r.n), N);
        return sPowRat(a, r, N);
      }
      // aᵇ = exp(b ln a)
      const lnA = ser({ k: 'fn', f: 'ln', a: t.a }, x, N + 4);
      const b = ser(t.b, x, N + 4);
      if (!lnA || !b) return null;
      return sExp(sMul(b, lnA), N);
    }
    case 'fn': {
      const u = ser(t.a, x, N);
      if (!u) return null;
      return serFn(t.f, u, N);
    }
    default:
      return null;
  }
}

/**
 * Le développement d'une écriture, assez précis pour l'ordre e voulu : on augmente N
 * jusqu'à ce que le reste soit d'ordre ≥ e (au plus e + 12).
 */
export function seriesTo(t: Term, x: string, e: number): Ser | null {
  const start = Math.max(e, 0) + 1;
  for (let N = start; N <= start + 12; N += 2) {
    const s = seriesOf(t, x, N);
    if (!s) return null;
    if (s.e >= e) return s;
  }
  return null;
}

/** deux développements : ils disent la même chose jusqu'au degré e (inclus) */
export function agreeTo(a: Ser, b: Ser, e: number): number | null {
  const degs = new Set<number>([...a.co.keys(), ...b.co.keys()]);
  let bad: number | null = null;
  for (const d of [...degs].sort((p, q) => p - q)) {
    if (d > e) continue;
    if (!Q.eq(coef(a, d), coef(b, d))) {
      bad = d;
      break;
    }
  }
  return bad;
}

/**
 * La limite d'un développement en 0 : le terme constant (reste d'ordre ≥ 0), ou ±∞ par le
 * premier terme de degré négatif (s = côté : 1 à droite, −1 à gauche, 0 les deux).
 */
export function serLimit(
  s: Ser,
  side: 1 | -1 | 0,
): { k: 'fin'; v: number } | { k: 'inf'; s: 1 | -1 } | null {
  const v = serVal(s);
  if (v !== null && v < 0) {
    const c = Q.sign(s.co.get(v)!);
    if (v % 2 === 0) return { k: 'inf', s: c > 0 ? 1 : -1 };
    if (!side) return null;
    return { k: 'inf', s: c * side > 0 ? 1 : -1 };
  }
  if (s.e < 0) return null;
  return { k: 'fin', v: Q.ratToNumber(coef(s, 0)) };
}

/**
 * La limite en 0 d'une écriture, par les développements limités : le développement de t
 * jusqu'à l'ordre 0 ; aᵇ (exposant variable) et e^u par la limite de b ln a, de u.
 */
export function seriesLimitOf(
  t: Term,
  x: string,
  side: 1 | -1 | 0,
): { k: 'fin'; v: number } | { k: 'inf'; s: 1 | -1 } | null {
  const hasVarX = (u: Term): boolean => {
    switch (u.k) {
      case 'sym':
        return u.n === x;
      case 'num':
        return false;
      case 'add':
      case 'mul':
        return u.c.some(hasVarX);
      case 'fn':
        return hasVarX(u.a);
      default:
        return hasVarX(u.a) || hasVarX(u.b);
    }
  };
  const viaExp = (u: Term) => {
    const L = seriesLimitOf(u, x, side);
    if (!L) return null;
    if (L.k === 'inf')
      return L.s > 0 ? ({ k: 'inf', s: 1 } as const) : ({ k: 'fin', v: 0 } as const);
    return { k: 'fin' as const, v: Math.exp(L.v) };
  };
  if (t.k === 'pow' && hasVarX(t.b)) {
    const u: Term = { k: 'mul', c: [t.b, { k: 'fn', f: 'ln', a: t.a }] };
    return viaExp(u);
  }
  if (t.k === 'fn' && t.f === 'exp') {
    const s = seriesTo(t, x, 0);
    if (s) return serLimit(s, side);
    return viaExp(t.a);
  }
  const s = seriesTo(t, x, 0);
  return s ? serLimit(s, side) : null;
}
